/**
 * 팝업 광고 상세/등록 페이지
 *
 * - id === 'new' : 신규 등록 모드
 * - 그 외         : 상세 조회/수정 모드
 *
 * 이미지 고정 사이즈: 340×620 (세로형 포트레이트)
 * 서버에서 680×1240(2×)으로 저장 후 앱에서 340×620으로 표시한다.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { popupService } from "../../services/popupService";
import { Popup, PopupFormData } from "../../types/popup";
import ConfirmModal from "../../components/ui/ConfirmModal";
import { InputField, SelectField } from "../../components/ui/FormField";
import DetailActionButtons from "../../components/ui/DetailActionButtons";
import BackButton from "../../components/ui/BackButton";
import EmptyDetailState from "../../components/ui/EmptyDetailState";
import { formatDateTime } from "../../utils/format";

export default function PopupDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  const locationPopup = location.state?.popup as Popup | undefined;
  const popup = isNew ? null : locationPopup;

  const [isEditing, setIsEditing] = useState(isNew);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<PopupFormData>({
    title: "",
    linkUrl: "",
    isVisible: true,
  });

  useEffect(() => {
    if (popup && !isNew) {
      setFormData({
        title: popup.title,
        linkUrl: popup.linkUrl,
        isVisible: popup.isVisible,
        imageUrl: popup.imageUrl,
      });
    }
  }, [popup, isNew]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setImageFile(undefined);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (popup) {
      setFormData({
        title: popup.title,
        linkUrl: popup.linkUrl,
        isVisible: popup.isVisible,
        imageUrl: popup.imageUrl,
      });
    }
  };

  const createMutation = useMutation({
    mutationFn: ({ data, file }: { data: PopupFormData; file?: File }) =>
      popupService.create(data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["popups"] });
      navigate("/admin/popup");
    },
    onError: () => {
      setIsSubmitting(false);
      alert("팝업 등록에 실패했습니다. 다시 시도해주세요.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ data, file }: { data: PopupFormData; file?: File }) =>
      popupService.update(Number(id), data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["popups"] });
      setIsEditing(false);
      setIsSubmitting(false);
      setImageFile(undefined);
      setImagePreview(null);
      window.history.replaceState(
        { ...window.history.state, usr: { popup: { ...popup, ...formData } } },
        ""
      );
    },
    onError: () => {
      setIsSubmitting(false);
      alert("팝업 수정에 실패했습니다. 다시 시도해주세요.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => popupService.delete(Number(id)),
    onSuccess: () => navigate("/admin/popup"),
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if (isSubmitting || createMutation.isPending || updateMutation.isPending) return;
    if (!formData.title) {
      alert("팝업 제목을 입력해주세요.");
      return;
    }
    if (isNew && !imageFile) {
      alert("팝업 이미지를 선택해주세요.");
      return;
    }
    setIsSubmitting(true);
    if (isNew) {
      createMutation.mutate({ data: formData, file: imageFile });
    } else {
      updateMutation.mutate({ data: formData, file: imageFile });
    }
  };

  const displayImage = imagePreview || formData.imageUrl;
  const isSaving = isSubmitting || createMutation.isPending || updateMutation.isPending;

  if (!popup && !isNew) {
    return <EmptyDetailState message="팝업 데이터를 찾을 수 없습니다" backTo="/admin/popup" />;
  }

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            {isNew ? "팝업 등록" : "팝업 상세"}
          </h1>
          <p className="text-gray-500 mt-2">
            {isNew ? "새 팝업 광고 등록" : `팝업 #${id}`}
          </p>
        </div>
        <BackButton to="/admin/popup" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 이미지 미리보기 — 340:620 고정 비율 */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
            <h2 className="text-sm font-bold text-gray-700 mb-3">
              <i className="fas fa-mobile-alt text-primary mr-2"></i>
              앱 표시 미리보기
              <span className="ml-2 text-xs font-normal text-gray-400">(340×620)</span>
            </h2>
            {displayImage ? (
              <div
                className="relative w-full overflow-hidden rounded-xl bg-gray-100"
                style={{ aspectRatio: "340/620" }}
              >
                <img
                  src={displayImage}
                  alt="팝업 미리보기"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = "none";
                  }}
                />
                {!formData.isVisible && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <span className="px-3 py-1 bg-gray-800 text-white text-xs font-bold rounded-full">
                      비활성
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div
                className="w-full rounded-xl bg-gray-100 flex items-center justify-center"
                style={{ aspectRatio: "340/620" }}
              >
                <div className="text-center text-gray-400">
                  <i className="fas fa-image text-4xl mb-2 block"></i>
                  <p className="text-xs">이미지를 선택하면<br />미리보기가 표시됩니다</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 폼 */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-6">

              <InputField
                label={<><i className="fas fa-heading text-primary mr-2"></i>팝업 제목 (관리용)</>}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                disabled={!isEditing}
                placeholder="예: 7월 여름 이벤트"
              />

              {/* 이미지 업로드 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-image text-primary mr-2"></i>
                  팝업 이미지
                  {isNew && <span className="ml-1 text-red-500">*</span>}
                </label>
                {isEditing ? (
                  <div
                    className="border-2 border-dashed border-primary/40 rounded-lg p-6 text-center cursor-pointer hover:border-primary/70 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                    {imageFile ? (
                      <div className="text-sm text-gray-700">
                        <i className="fas fa-check-circle text-primary mr-2"></i>
                        {imageFile.name}
                        <span className="ml-2 text-gray-400">
                          ({(imageFile.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                    ) : (
                      <>
                        <i className="fas fa-cloud-upload-alt text-3xl text-gray-300 mb-2"></i>
                        <p className="text-sm text-gray-500">클릭하여 이미지 파일 선택</p>
                        {formData.imageUrl && (
                          <p className="text-xs text-gray-400 mt-1">
                            선택하지 않으면 기존 이미지가 유지됩니다
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData.imageUrl || "이미지 없음"}
                    disabled
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-bg-light text-gray-500 text-sm"
                  />
                )}
                <p className="mt-2 text-xs text-gray-500">
                  <i className="fas fa-info-circle mr-1"></i>
                  권장 사이즈: 340×620px (세로형) · 서버에서 자동으로 2배 크기(680×1240)로 저장됩니다
                </p>
              </div>

              <InputField
                label={<><i className="fas fa-link text-primary mr-2"></i>링크 URL (선택)</>}
                type="url"
                value={formData.linkUrl || ""}
                onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                disabled={!isEditing}
                placeholder="https://example.com"
                hint="팝업 터치 시 이동할 URL (없으면 단순 닫기)"
              />

              <SelectField
                label={<><i className="fas fa-toggle-on text-primary mr-2"></i>노출 상태</>}
                value={formData.isVisible ? "true" : "false"}
                onChange={(e) =>
                  setFormData({ ...formData, isVisible: e.target.value === "true" })
                }
                disabled={!isEditing}
              >
                <option value="true">노출 중 (앱 진입 시 랜덤 노출 대상)</option>
                <option value="false">숨김 (앱에 노출 안 됨)</option>
              </SelectField>

              {!isNew && popup?.createdAt && popup?.updatedAt && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label={<><i className="fas fa-calendar-plus text-primary mr-2"></i>등록일</>}
                    value={formatDateTime(popup.createdAt)}
                    disabled
                  />
                  <InputField
                    label={<><i className="fas fa-calendar-check text-primary mr-2"></i>수정일</>}
                    value={formatDateTime(popup.updatedAt)}
                    disabled
                  />
                </div>
              )}
            </div>

            <DetailActionButtons
              isNew={isNew}
              isEditing={isEditing}
              isSaving={isSaving}
              onEdit={handleEdit}
              onSave={handleSave}
              onCancelEdit={handleCancelEdit}
              onDelete={() => setShowDeleteModal(true)}
            />
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <ConfirmModal
          title="팝업을 삭제하시겠습니까?"
          confirmLabel="삭제"
          isLoading={deleteMutation.isPending}
          onConfirm={() => {
            deleteMutation.mutate();
            setShowDeleteModal(false);
          }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}
